import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';
// import showToast from '../utils/toastHelper';

const PublicElection = () => {
  const { id } = useParams();
  const [election, setElection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [selectedVote, setSelectedVote] = useState(null); // { positionId, candidateId }
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmittingVote, setIsSubmittingVote] = useState(false);

  useEffect(() => {
    fetchElection();

    // Poll every 60s — guest voters are redirected to payment immediately
    // so sub-second freshness is not needed here.
    let interval = setInterval(fetchElection, 60000);

    // Page Visibility API: pause polling when the tab is hidden,
    // resume (with an immediate fetch) when the user comes back.
    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        fetchElection();
        interval = setInterval(fetchElection, 60000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchElection = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/elections/${id}/public`); 
      const freshData = response.data.data;
      
      // Force new object reference to trigger re-render
      setElection({...freshData});
    } catch (error) {
      console.error('Failed to fetch election:', error);
      // Don't show toast - error page will be displayed on initial load
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'secondary',
      active: 'success',
      completed: 'info',
      cancelled: 'danger',
      upcoming: 'warning',
    };
    return `badge bg-${badges[status] || 'secondary'}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleGuestVoteClick = (positionId, candidateId) => {
    setSelectedVote({ positionId, candidateId });
    setShowPhoneModal(true);
    // Pre-fill phone number from localStorage if available
    const savedPhone = localStorage.getItem('guestPhone');
    setPhoneNumber(savedPhone || '');
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber.trim()) {
      alert('Please enter your phone number');
      return;
    }

    setIsSubmittingVote(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/votes/guest`,
        {
          candidateId: selectedVote.candidateId,
          positionId: selectedVote.positionId,
          phoneNumber: phoneNumber.trim(),
        }
      );

      if (response.data.success) {
        // Store election ID to return after payment
        localStorage.setItem('pendingGuestVoteElectionId', id);
        // Save phone number for future votes in this browser
        localStorage.setItem('guestPhone', phoneNumber.trim());
        // Redirect to payment
        window.location.href = response.data.data.paymentLink;
      }
    } catch (error) {
      console.error('Failed to initiate guest vote:', error);
      alert(error.response?.data?.message || 'Failed to initiate vote. Please try again.');
      setIsSubmittingVote(false);
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-center">
          <div className="spinner-border text-white" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!election) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-center text-white">
          <i className="bi bi-exclamation-circle" style={{ fontSize: '4rem' }}></i>
          <h3 className="mt-3">Election Not Found</h3>
          <p>The election you're looking for doesn't exist or is not publicly accessible.</p>
          <Link to="/login" className="btn btn-light mt-3">Go to Login</Link>
        </div>
      </div>
    );
  }

  // Check if election has ended
  const now = new Date();
  const hasEnded = now > new Date(election.endDate);
  const hasStarted = now >= new Date(election.startDate);
  
  // Determine actual status based on dates
  const getActualStatus = () => {
    if (election.status === 'cancelled') return 'cancelled';
    if (election.status === 'draft') return 'draft';
    if (hasEnded) return 'completed';
    if (!hasStarted) return 'upcoming';
    return 'active';
  };
  
  const actualStatus = getActualStatus();
  const statusLabel = actualStatus === 'upcoming' ? 'Upcoming' : actualStatus;

  return (
    <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="container py-5">
        {/* Header */}
        <div className="text-white mb-4">
          <Logo color="white" width="300px" />
        </div>

        {/* Election Info Card */}
        <div className="card mb-5 shadow-lg" style={{ borderRadius: '12px', border: 'none' }}>
          <div className="card-body p-4">
            {/* Election Title, Status, and Details */}
            <div className="row align-items-center mb-4">
              {/* Left side - Title, Badge, Description */}
              <div className="col-lg-6 mb-4 mb-lg-0">
                <h2 className="mb-2" style={{ fontWeight: '700', color: '#2c3e50', fontSize: '2rem' }}>{election.name}</h2>
                <div className="mb-3">
                  <span className={getStatusBadge(actualStatus)} style={{ fontSize: '0.875rem', textTransform: 'capitalize', padding: '0.5rem 1.5rem', borderRadius: '20px' }}>
                    {statusLabel}
                  </span>
                </div>
                {election.description && (
                  <p className="mb-0" style={{ fontSize: '1rem', color: '#6b7280', lineHeight: '1.6' }}>{election.description}</p>
                )}
              </div>

              {/* Right side - Date and Positions */}
              <div className="col-lg-6">
                <div className="row text-center">
                  {/* Metric 1: Start Date */}
                  <div className="col-4 mb-3 mb-md-0">
                    <div className="d-flex flex-column align-items-center">
                      <div className="mb-2" style={{ 
                        width: '48px', 
                        height: '48px', 
                        borderRadius: '50%', 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                      }}>
                        <i className="bi bi-calendar-event text-white" style={{ fontSize: '1.25rem' }}></i>
                      </div>
                      <small className="text-muted text-uppercase" style={{ fontWeight: '600', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Start Date</small>
                      <strong style={{ fontSize: '0.875rem', marginTop: '0.5rem', color: '#2c3e50' }}>{formatDate(election.startDate)}</strong>
                    </div>
                  </div>
                  {/* Metric 2: End Date */}
                  <div className="col-4 mb-3 mb-md-0">
                    <div className="d-flex flex-column align-items-center">
                      <div className="mb-2" style={{ 
                        width: '48px', 
                        height: '48px', 
                        borderRadius: '50%', 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                      }}>
                        <i className="bi bi-calendar-check text-white" style={{ fontSize: '1.25rem' }}></i>
                      </div>
                      <small className="text-muted text-uppercase" style={{ fontWeight: '600', fontSize: '0.75rem', letterSpacing: '0.5px' }}>End Date</small>
                      <strong style={{ fontSize: '0.875rem', marginTop: '0.5rem', color: '#2c3e50' }}>{formatDate(election.endDate)}</strong>
                    </div>
                  </div>
                  {/* Metric 3: Categories (Original Trophy icon) */}
                  <div className="col-4">
                    <div className="d-flex flex-column align-items-center">
                      <div className="mb-2" style={{ 
                        width: '48px', 
                        height: '48px', 
                        borderRadius: '50%', 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                      }}>
                        <i className="bi bi-trophy text-white" style={{ fontSize: '1.25rem' }}></i>
                      </div>
                      <small className="text-muted text-uppercase" style={{ fontWeight: '600', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Categories</small>
                      <strong style={{ fontSize: '0.875rem', marginTop: '0.5rem', color: '#2c3e50' }}>{election.positions?.length || 0}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* added Login Link inside the Card */}
            {!hasEnded && (
              <div className="row mt-4 pt-3 border-top">
                  <div className="col-12 text-center">
                      <Link to="/login" className="btn btn-light btn-lg" style={{ 
                          fontWeight: '600'
                      }}>
                          <i className="bi bi-box-arrow-in-right me-2"></i>
                          Login to Vote
                      </Link>
                  </div>
              </div>
            )}
            {/* END: Login Link inside the Card */}
          </div>
        </div>

        {/* Categories and Candidates */}
        <div className="row">
          {election.positions && election.positions.filter(p => p.candidates && p.candidates.length > 0).length > 0 ? (
            election.positions.filter(p => p.candidates && p.candidates.length > 0).map((position) => (
              <div key={position.id} className="col-12 mb-5">
                <div className="card shadow-sm" style={{ borderRadius: '12px', border: 'none' }}>
                  <div className="card-header" style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                    borderRadius: '12px 12px 0 0',
                    padding: '1.5rem'
                  }}>
                    <h4 className="mb-0 text-white" style={{ fontWeight: '700' }}>{position.name}</h4>
                    {position.description && (
                      <p className="mb-0 mt-2 text-white" style={{ fontSize: '0.938rem', opacity: 0.95 }}>{position.description}</p>
                    )}
                  </div>
                  <div className="card-body p-4">
                    {position.candidates && position.candidates.length > 0 ? (
                      <div className="row g-4">
                        {position.candidates
                          .sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0))
                          .map((candidate, index) => (
                          <div key={candidate.id} className="col-md-6 col-lg-4">
                            <div className="h-100" style={{
                              background: '#ffffff',
                              borderRadius: '8px', 
                              border: '2px solid #f1f3f5',
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                              overflow: 'hidden'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-8px)';
                              e.currentTarget.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.3)';
                              e.currentTarget.style.borderColor = '#667eea';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.borderColor = '#f1f3f5';
                            }}>
                              {candidate.photoUrl ? (
                                <img
                                  src={candidate.photoUrl}
                                  alt={candidate.user?.fullName}
                                  style={{
                                    width: '100%',
                                    height: '280px',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: '100%',
                                    height: '280px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '5rem',
                                    fontWeight: '700'
                                  }}
                                >
                                  {candidate.user?.fullName?.charAt(0) || '?'}
                                </div>
                              )}
                              <div style={{ padding: '1.5rem' }}>
                                <h5 className="mb-2" style={{ fontWeight: '700', color: '#2c3e50', fontSize: '1.25rem' }}>{candidate.user?.fullName || 'Unknown'}</h5>
                                {candidate.bio && (
                                  <p className="mb-3" style={{ fontSize: '0.938rem', color: '#6b7280', lineHeight: '1.7' }}>
                                    {candidate.bio.length > 150 ? `${candidate.bio.substring(0, 150)}...` : candidate.bio}
                                  </p>
                                )}
                                <div className="d-flex justify-content-between align-items-center pt-3" style={{ borderTop: '1px solid #f1f3f5' }}>
                                  <div>
                                    <div className="d-flex align-items-center gap-2">
                                      <i className="bi bi-bar-chart-fill" style={{ color: '#667eea', fontSize: '1.125rem' }}></i>
                                      <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Votes:</span>
                                      <span style={{ fontSize: '1rem', fontWeight: '700', color: '#2c3e50' }}>{candidate.voteCount || 0}</span>
                                    </div>
                                  </div>
                                  {candidate.voteCount > 0 && (
                                    <div className="d-flex align-items-center gap-2">
                                      <span style={{ 
                                        background: index === 0 ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' : '#f3f4f6',
                                        color: index === 0 ? 'white' : '#6b7280',
                                        padding: '0.25rem 0.75rem',
                                        borderRadius: '12px',
                                        fontSize: '0.813rem',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem'
                                      }}>
                                        {index === 0 && <i className="bi bi-trophy-fill"></i>}
                                        #{index + 1}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                {/* Guest Voting Button */}
                                {election.allowGuestVoting && !hasEnded && hasStarted && election.status === 'active' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleGuestVoteClick(position.id, candidate.id);
                                    }}
                                    className="btn w-100 mt-3"
                                    style={{
                                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                      color: 'white',
                                      border: 'none',
                                      padding: '0.625rem',
                                      fontSize: '0.875rem',
                                      fontWeight: '500',
                                    }}
                                  >
                                    <i className="bi bi-hand-thumbs-up me-1"></i>
                                    Vote ({election.currency} {election.votePrice})
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <div className="mb-3" style={{ 
                          width: '80px', 
                          height: '80px', 
                          borderRadius: '50%', 
                          background: '#f8f9fa', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          margin: '0 auto'
                        }}>
                          <i className="bi bi-person-x text-muted" style={{ fontSize: '2.5rem' }}></i>
                        </div>
                        <p className="mb-0 text-muted" style={{ fontSize: '1rem' }}>No candidates yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12">
              <div className="card shadow-sm" style={{ borderRadius: '12px', border: 'none' }}>
                <div className="card-body text-center py-5">
                  <div className="mb-3" style={{ 
                    width: '100px', 
                    height: '100px', 
                    borderRadius: '50%', 
                    background: '#f8f9fa', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    margin: '0 auto'
                  }}>
                    <i className="bi bi-trophy text-muted" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <p className="mt-3 text-muted" style={{ fontSize: '1.125rem' }}>No positions have been added to this election yet.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!hasEnded && !election.allowGuestVoting && (
          <div className="text-center mt-5 mb-4">
            <Link to="/login" className="btn btn-light btn-lg" style={{ 
              fontWeight: '600'
            }}>
              <i className="bi bi-box-arrow-in-right me-2"></i>
              Login to Vote
            </Link>
          </div>
        )}

        {/* Phone Number Modal for Guest Voting */}
        {showPhoneModal && (
          <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content" style={{ borderRadius: '16px', border: 'none' }}>
                  <div className="modal-header" style={{ borderBottom: '2px solid #f1f5f9', padding: '1.5rem' }}>
                    <h5 className="modal-title" style={{ fontWeight: '700', color: '#2c3e50' }}>
                      <i className="bi bi-phone me-2" style={{ color: '#667eea' }}></i>
                      Enter Your Phone Number
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        setShowPhoneModal(false);
                        setSelectedVote(null);
                        setPhoneNumber('');
                      }}
                      disabled={isSubmittingVote}
                    ></button>
                  </div>
                  <form onSubmit={handlePhoneSubmit}>
                    <div className="modal-body" style={{ padding: '1.5rem' }}>
                      <p style={{ fontSize: '0.938rem', color: '#6b7280', marginBottom: '1.5rem' }}>
                        Enter your phone number to proceed with voting. You will be redirected to complete payment.
                      </p>
                      <div className="mb-3">
                        <label className="form-label" style={{ fontWeight: '600', fontSize: '0.875rem', color: '#4b5563' }}>
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          placeholder="e.g., +256700000000"
                          required
                          disabled={isSubmittingVote}
                          style={{
                            padding: '0.75rem',
                            fontSize: '0.938rem',
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                        <small className="text-muted" style={{ fontSize: '0.813rem' }}>
                          Include country code (e.g., +256 for Uganda)
                        </small>
                      </div>
                      {election && (
                        <div className="alert" style={{ 
                          background: '#f0f9ff', 
                          border: '1px solid #bae6fd',
                          borderRadius: '8px',
                          padding: '1rem'
                        }}>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <i className="bi bi-info-circle" style={{ color: '#0284c7', fontSize: '1.25rem' }}></i>
                            <strong style={{ color: '#0369a1', fontSize: '0.875rem' }}>Payment Required</strong>
                          </div>
                          <p className="mb-0" style={{ fontSize: '0.813rem', color: '#075985' }}>
                            Vote Price: <strong>{election.currency} {election.votePrice}</strong>
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="modal-footer" style={{ borderTop: '2px solid #f1f5f9', padding: '1.5rem' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowPhoneModal(false);
                          setSelectedVote(null);
                          setPhoneNumber('');
                        }}
                        disabled={isSubmittingVote}
                        style={{
                          padding: '0.625rem 1.5rem',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn"
                        disabled={isSubmittingVote}
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '0.625rem 1.5rem',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}
                      >
                        {isSubmittingVote ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Processing...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-credit-card me-2"></i>
                            Proceed to Payment
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div
              className="modal-backdrop fade show"
              onClick={() => {
                if (!isSubmittingVote) {
                  setShowPhoneModal(false);
                  setSelectedVote(null);
                  setPhoneNumber('');
                }
              }}
            ></div>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicElection;
